import React, { FC, useContext } from "react";
import { GridLegacy as Grid, useMediaQuery, useTheme } from "@mui/material";

import { isSysAdmin } from "../../utility/utils";
import { IVehicleRecordOptions, IPreferences } from "../../logic/api";

import EditableField, { EditableFieldTypes } from "../../components/editable/EditableField/EditableField";
import EditableGroup, { EditableGroupType } from "../../components/editable/EditableGroup/EditableGroup";
import NotchedCard from "../../components/cards/NotchedCard/NotchedCard";

import { SessionContext } from "../../utility/useSession";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { ReleaseFlag, useReleaseManagement } from "../../utility/feature-management";

interface IVehicleRecordSettingsProps {
  onSave: (data: Partial<IPreferences>) => Promise<boolean>;
}

export const VehicleRecordSettings: FC<IVehicleRecordSettingsProps> = ({ onSave }) => {
  const session = useContext(SessionContext);
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("xs"));
  const { t } = useTranslation();
  const isAdditionalCustomizableFieldsReleased = useReleaseManagement(ReleaseFlag.releaseAdditionalCustomizableFields);
  const isStoreFuelTypeReleased = useReleaseManagement(ReleaseFlag.releaseStoreFuelType);
  const isDisplayCleanAirComplianceReleased = useReleaseManagement(ReleaseFlag.releaseDisplayCleanAirCompliance);

  if (!session.current.site) {
    return null;
  }

  if (!isSysAdmin(session)) {
    return null;
  }

  const labelStyle = isSmallDevice
    ? { textAlign: "center" as const }
    : { paddingTop: 40 };

  const ensureVehicleRecordOptions = (data: IPreferences): IVehicleRecordOptions => {
    if (!data.vehicleRecordOptions) {
      data.vehicleRecordOptions = { properties: {} };
    }

    return data.vehicleRecordOptions;
  };

  return (
    <Grid item xs={12}>
      <NotchedCard label={t("settings.vehicleRecordOptions")} style={styles.card}>
        <EditableGroup<IPreferences>
          id="vehicleRecordOptions"
          editType={EditableGroupType.switchableEditing}
          data={session.current.site.preferences}
          onDataChanged={(data) => onSave({
            vehicleRecordOptions: data.vehicleRecordOptions
          })}
          testId="vehicle-record-options"
        >
          <Grid container>
            <Grid item sm={8} xs={12}>
              <div style={labelStyle}><strong>{t("settings.model")}</strong></div>
            </Grid>
            <Grid item sm={2} xs={6}>
              <EditableField<IPreferences, boolean>
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.switch}
                label={t("settings.visible")}
                onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.model)}
                onChange={(data, value) => ensureVehicleRecordOptions(data).properties.model = value ? { required: false } : undefined}
                testId="settings-vehicle-record-model-visible-switch"
              />
            </Grid>
            <Grid item sm={2} xs={6}>
              <EditableField<IPreferences, boolean>
                useFullWidthWhenEditing={false}
                inputType={EditableFieldTypes.switch}
                label={t("settings.mandatory")}
                onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.model?.required)}
                onChange={(data, value) => ensureVehicleRecordOptions(data).properties.model = { required: Boolean(value) }}
                testId="settings-vehicle-record-model-mandatory-switch"
              />
            </Grid>
          </Grid>
          {isAdditionalCustomizableFieldsReleased && (<>
            <Grid container>
              <Grid item sm={8} xs={12}>
                <div style={labelStyle}><strong>{t("settings.make")}</strong></div>
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.visible")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.make)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.make = value ? { required: false } : undefined}
                  testId="settings-vehicle-record-make-visible-switch"
                />
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.mandatory")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.make?.required)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.make = { required: Boolean(value) }}
                  testId="settings-vehicle-record-make-mandatory-switch"
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item sm={8} xs={12}>
                <div style={labelStyle}><strong>{t("settings.colour")}</strong></div>
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.visible")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.colour)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.colour = value ? { required: false } : undefined}
                  testId="settings-vehicle-record-colour-visible-switch"
                />
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.mandatory")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.colour?.required)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.colour = { required: Boolean(value) }}
                  testId="settings-vehicle-record-colour-mandatory-switch"
                />
              </Grid>
            </Grid>
          </>)}
          {isStoreFuelTypeReleased && (
            <Grid container>
              <Grid item sm={8} xs={12}>
                <div style={labelStyle}><strong>{t("settings.fuelType")}</strong></div>
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.visible")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.fuelType)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.fuelType = value ? { required: false } : undefined}
                  testId="settings-vehicle-record-fuel-type-visible-switch"
                />
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.mandatory")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.fuelType?.required)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.fuelType = { required: Boolean(value) }}
                  testId="settings-vehicle-record-fuel-type-mandatory-switch"
                />
              </Grid>
            </Grid>
          )}
          {isDisplayCleanAirComplianceReleased && (
            <Grid container>
              <Grid item sm={8} xs={12}>
                <div style={labelStyle}><strong>{t("booking.ulezCompliance.label")}</strong></div>
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.visible")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.cleanAirCompliance)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.cleanAirCompliance = value ? { required: false } : undefined}
                  testId="settings-vehicle-record-clean-air-compliance-visible-switch"
                />
              </Grid>
              <Grid item sm={2} xs={6}>
                <EditableField<IPreferences, boolean>
                  useFullWidthWhenEditing={false}
                  inputType={EditableFieldTypes.switch}
                  label={t("settings.mandatory")}
                  onProvide={(data) => Boolean(data.vehicleRecordOptions?.properties.cleanAirCompliance?.required)}
                  onChange={(data, value) => ensureVehicleRecordOptions(data).properties.cleanAirCompliance = { required: Boolean(value) }}
                  testId="settings-vehicle-record-clean-air-compliance-mandatory-switch"
                />
              </Grid>
            </Grid>
          )}
        </EditableGroup>
      </NotchedCard>
    </Grid>
  );
};
